import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Cache for signed URLs to avoid unnecessary API calls
const urlCache = new Map<string, { url: string; expires: number }>();

export function useProfilePhotoUrl(photoUrl: string | null | undefined): string | null {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!photoUrl) {
      setResolvedUrl(null);
      return;
    }

    // If it's already a public URL, use directly
    if (photoUrl.includes('/object/public/')) {
      setResolvedUrl(photoUrl);
      return;
    }
    
    // Check if it needs a signed URL (private bucket references)
    const needsSignedUrl = photoUrl.startsWith('private:') || 
      (photoUrl.includes('caregiver-documents/') && !photoUrl.includes('/object/public/'));

    // If it doesn't need a signed URL, use directly
    if (!needsSignedUrl) {
      setResolvedUrl(photoUrl);
      return;
    }

    // Check cache
    const cached = urlCache.get(photoUrl);
    if (cached && cached.expires > Date.now()) {
      setResolvedUrl(cached.url);
      return;
    }

    // Need to fetch signed URL
    const fetchSignedUrl = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-profile-photo-url', {
          body: { photoUrl }
        });

        if (!error && data?.signedUrl) {
          // Cache for 55 minutes (signed URLs expire in 1 hour)
          urlCache.set(photoUrl, {
            url: data.signedUrl,
            expires: Date.now() + 55 * 60 * 1000
          });
          setResolvedUrl(data.signedUrl);
        } else {
          // Show nothing on error (let fallback handle it)
          setResolvedUrl(null);
        }
      } catch (err) {
        console.error('Error fetching signed URL:', err);
        setResolvedUrl(null);
      }
    };

    fetchSignedUrl();
  }, [photoUrl]);

  return resolvedUrl;
}

// Batch version for multiple photos
export function useProfilePhotoUrls(photoUrls: (string | null | undefined)[]): (string | null)[] {
  const [resolvedUrls, setResolvedUrls] = useState<(string | null)[]>([]);

  useEffect(() => {
    const resolveUrls = async () => {
      const results = await Promise.all(
        photoUrls.map(async (url) => {
          if (!url) return null;

          // If it's already a public URL, use directly
          if (url.includes('/object/public/')) {
            return url;
          }
          
          // Check if it needs a signed URL (private bucket references)
          const needsSigning = url.startsWith('private:') || 
            (url.includes('caregiver-documents/') && !url.includes('/object/public/'));
          if (!needsSigning) {
            return url;
          }

          // Check cache
          const cached = urlCache.get(url);
          if (cached && cached.expires > Date.now()) {
            return cached.url;
          }

          try {
            const { data, error } = await supabase.functions.invoke('get-profile-photo-url', {
              body: { photoUrl: url }
            });

            if (!error && data?.signedUrl) {
              urlCache.set(url, {
                url: data.signedUrl,
                expires: Date.now() + 55 * 60 * 1000
              });
              return data.signedUrl;
            }
          } catch (err) {
            console.error('Error fetching signed URL:', err);
          }
          
          return null; // Return null on error so fallback is shown
        })
      );

      setResolvedUrls(results);
    };

    resolveUrls();
  }, [JSON.stringify(photoUrls)]);

  return resolvedUrls;
}